import React, { useState, useEffect, useRef, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarDays, ChevronUp, ChevronDown, AlertTriangle } from "lucide-react";

const MONTHS_FULL = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const MONTHS_SHORT = ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"];

const ITEM_H = 40; // altezza ogni riga in px
const VISIBLE_ROWS = 3; // righe visibili (1 sopra, 1 centro, 1 sotto)
const CENTER = Math.floor(VISIBLE_ROWS / 2); // indice riga centrale (1)

const parseISO = (iso) => {
  if (!iso) { const n = new Date(); return { d: n.getDate(), m: n.getMonth(), y: n.getFullYear() }; }
  const p = iso.split("-");
  return { y: parseInt(p[0]), m: parseInt(p[1]) - 1, d: parseInt(p[2]) || 1 };
};

/* ─────────────────────────────────────────────────────────────────────────────
   SpinnerColumn
   - Mostra 5 righe visibili (2 sopra, 1 centro, 2 sotto).
   - Animazione CSS transform: le righe scorrono fluidamente.
   - Wheel: accumula deltaY, un passo ogni 80px (no salti rapidi trackpad).
   - Touch: drag live con snapping al rilascio.
   - NON usa overflow-y-auto → nessun conflitto con Radix/iOS.
──────────────────────────────────────────────────────────────────────────────*/
const SpinnerColumn = ({ items, value, onChange, testId }) => {
  const idx = items.findIndex(i => i.value === value);
  const safeIdx = Math.max(0, idx < 0 ? 0 : idx);

  // Refs "live" per handler imperativi (evitano closure stale)
  const safeIdxRef = useRef(safeIdx);
  const itemsRef   = useRef(items);
  const changeRef  = useRef(onChange);
  useEffect(() => { safeIdxRef.current = safeIdx; }, [safeIdx]);
  useEffect(() => { itemsRef.current   = items;    }, [items]);
  useEffect(() => { changeRef.current  = onChange; }, [onChange]);

  const wrapRef        = useRef(null);
  const dragOffsetRef  = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging,  setIsDragging] = useState(false);

  // Funzione stabile per navigare all'indice target
  const goTo = useCallback((targetIdx) => {
    const its  = itemsRef.current;
    const next = Math.max(0, Math.min(its.length - 1, targetIdx));
    changeRef.current(its[next].value);
  }, []);

  // Listener imperativi (non-passive dove serve preventDefault)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    /* ---- Mouse wheel ---- */
    let wheelAcc   = 0;
    let wheelTimer = null;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
      wheelAcc += e.deltaY;
      if (wheelTimer) clearTimeout(wheelTimer);
      const steps = Math.trunc(wheelAcc / 80); // 1 passo ogni 80px accumulati
      if (Math.abs(steps) >= 1) {
        goTo(safeIdxRef.current + steps);
        wheelAcc -= steps * 80;
      }
      // Reset accumulatore se la rotella si ferma
      wheelTimer = setTimeout(() => { wheelAcc = 0; }, 400);
    };

    /* ---- Touch drag ---- */
    let startY   = null;
    let startIdx = null;
    const onTouchStart = (e) => {
      startY   = e.touches[0].clientY;
      startIdx = safeIdxRef.current;
      dragOffsetRef.current = 0;
      setDragOffset(0);
      setIsDragging(true);
    };
    const onTouchMove = (e) => {
      e.preventDefault(); // blocca scroll pagina durante il drag
      if (startY === null) return;
      const offset = e.touches[0].clientY - startY;
      dragOffsetRef.current = offset;
      setDragOffset(offset);
    };
    const onTouchEnd = () => {
      if (startY === null) return;
      const steps = -Math.round(dragOffsetRef.current / ITEM_H);
      goTo(startIdx + steps);
      dragOffsetRef.current = 0;
      setDragOffset(0);
      setIsDragging(false);
      startY = null;
    };

    el.addEventListener("wheel",      onWheel,      { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true  });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd);

    return () => {
      el.removeEventListener("wheel",      onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
      if (wheelTimer) clearTimeout(wheelTimer);
    };
  }, [goTo]);

  const go = (delta) => goTo(safeIdx + delta);

  // translateY: porta l'item[safeIdx] al centro della finestra
  const translateY = (CENTER - safeIdx) * ITEM_H + dragOffset;

  return (
    <div className="flex-1 flex flex-col items-center select-none">
      {/* Chevron su */}
      <button
        onClick={() => go(-1)}
        disabled={safeIdx === 0}
        className="w-full h-8 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
        data-testid={`${testId}-prev`}
      >
        <ChevronUp className="w-4 h-4" />
      </button>

      {/* Finestra visibile: VISIBLE_ROWS righe × ITEM_H, overflow hidden (non auto) */}
      <div
        ref={wrapRef}
        className="relative w-full"
        style={{ height: VISIBLE_ROWS * ITEM_H, overflow: "hidden", cursor: "ns-resize" }}
      >
        {/* Colonna scorrevole con animazione CSS */}
        <div
          style={{
            transform:  `translateY(${translateY}px)`,
            transition: isDragging ? "none" : "transform 280ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            willChange: "transform",
          }}
        >
          {items.map((item, i) => {
            const dist     = Math.abs(i - safeIdx);
            const isCenter = dist === 0;
            return (
              <div key={item.value} style={{ height: ITEM_H }} className="flex items-center justify-center">
                <button
                  onClick={() => onChange(item.value)}
                  data-active={isCenter}
                  data-testid={isCenter ? `${testId}-${item.value}` : undefined}
                  className={[
                    "w-full h-full flex items-center justify-center transition-all duration-150",
                    isCenter
                      ? "text-base font-bold text-foreground"
                      : dist === 1
                        ? "text-sm text-muted-foreground hover:text-foreground"
                        : "text-xs text-muted-foreground/40 hover:text-muted-foreground",
                  ].join(" ")}
                >
                  {item.label}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chevron giù */}
      <button
        onClick={() => go(1)}
        disabled={safeIdx === items.length - 1}
        className="w-full h-8 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"
        data-testid={`${testId}-next`}
      >
        <ChevronDown className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   DateInput
──────────────────────────────────────────────────────────────────────────────*/
export const DateInput = ({ value, onChange, label, testId = "date-input", monthOnly = false, disablePast = false, pastErrorMessage = "Prodotto già scaduto — seleziona una data futura" }) => {
  const [open, setOpen] = useState(false);
  const parsed = parseISO(value);
  const [day,   setDay]   = useState(parsed.d);
  const [month, setMonth] = useState(parsed.m);
  const [year,  setYear]  = useState(parsed.y);
  const [pastError, setPastError] = useState(false);

  // Sync se value cambia dall'esterno
  useEffect(() => {
    const p = parseISO(value);
    setDay(p.d); setMonth(p.m); setYear(p.y);
  }, [value]);

  // Clamp day se cambia mese/anno
  useEffect(() => {
    if (monthOnly) return;
    const max = new Date(year, month + 1, 0).getDate();
    if (day > max) setDay(max);
  }, [month, year, day, monthOnly]);

  // Validazione data passata
  useEffect(() => {
    if (!disablePast || monthOnly) { setPastError(false); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    setPastError(new Date(year, month, day) < today);
  }, [day, month, year, disablePast, monthOnly]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days   = Array.from({ length: daysInMonth }, (_, i) => ({ value: i + 1, label: String(i + 1).padStart(2, "0") }));
  const months = MONTHS_FULL.map((_, i) => ({ value: i, label: MONTHS_FULL[i].substring(0, 3) }));
  const curY   = new Date().getFullYear();
  const years  = Array.from({ length: 20 }, (_, i) => ({ value: curY - 2 + i, label: String(curY - 2 + i) }));

  const handleConfirm = () => {
    if (disablePast && pastError) return;
    onChange(monthOnly
      ? `${year}-${String(month + 1).padStart(2, "0")}`
      : `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    );
    setOpen(false);
  };

  const displayDate = value
    ? monthOnly
      ? `${MONTHS_FULL[parsed.m]} ${parsed.y}`
      : `${parsed.d} ${MONTHS_SHORT[parsed.m]} ${parsed.y}`
    : "Seleziona data";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex h-10 w-full min-w-0 items-center gap-2 overflow-hidden rounded-xl border border-border bg-background px-3 py-2 text-sm
            transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid={`${testId}-trigger`}
        >
          <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className={`min-w-0 truncate ${value ? "text-foreground font-medium" : "text-muted-foreground"}`}>
            {displayDate}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[280px] max-w-[calc(100vw-16px)] max-h-[var(--radix-popover-content-available-height)] p-0 rounded-2xl shadow-xl border-border bg-background overflow-hidden flex flex-col"
        align="center" sideOffset={8}
        avoidCollisions collisionPadding={12}
        collisionBoundary={typeof document !== "undefined" ? document.body : undefined}
      >
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
          {label && <h4 className="font-semibold text-sm text-center text-foreground">{label}</h4>}

          {/* Colonne spinner */}
          <div className="relative flex justify-between rounded-xl border border-border bg-muted/10">
            <div
              className={`absolute left-0 right-0 pointer-events-none border-y transition-colors
                ${pastError ? "bg-red-500/10 border-red-400/40" : "bg-primary/10 border-primary/20"}`}
              style={{ top: 32 + CENTER * ITEM_H, height: ITEM_H }}
            />
            {!monthOnly && (
              <>
                <SpinnerColumn items={days}   value={day}   onChange={setDay}   testId="day" />
                <div className="w-px bg-border/40 my-3" />
              </>
            )}
            <SpinnerColumn items={months} value={month} onChange={setMonth} testId="month" />
            <div className="w-px bg-border/40 my-3" />
            <SpinnerColumn items={years}  value={year}  onChange={setYear}  testId="year" />
          </div>

        </div>

        {/* Footer fisso: errore e conferma restano visibili anche su mobile */}
        <div className="shrink-0 p-3 pt-2 border-t border-border/50 bg-background space-y-2">
          {disablePast && pastError && (
            <div
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[11px] font-semibold leading-4 text-red-700 sm:text-xs"
              data-testid={`${testId}-past-date-error`}
              role="alert"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{pastErrorMessage}</span>
            </div>
          )}
          <button
            onClick={handleConfirm}
            disabled={disablePast && pastError}
            className="w-full h-11 bg-primary text-primary-foreground font-semibold rounded-xl text-sm
              transition-transform active:scale-[0.98] hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring
              disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100"
            data-testid={`${testId}-confirm`}
          >
            Conferma
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateInput;
